import { SettingsIcon } from '@/components/layout/SettingsIcon';

import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, userCan } from '@/components/auth/AuthContext';

import { getAccountProfile } from '@/modules/accounts/api';
import { useAccount } from '@/modules/accounts/hooks';
import { useNeedsYou } from '@/pages/authenticated/home/useNeedsYou';
import { TierWidget } from '@/components/tier/TierWidget';
import { SetupChecklistWidget } from '@/components/setup/SetupChecklistWidget';
import { ClayCountBadge } from '@/components/ClayPunctuation';
import { useCommandPalette } from '@/components/command/useCommandPalette';
import { QUICK_ACTIONS_SHORTCUT } from '@/components/command/shortcuts';
import { useNextPaidTier, useTierLabel, useTierSummary } from '@/modules/billing/hooks';
import { useUpgradeModal } from '@/components/tier/UpgradeModal';
import { useTheme } from '@/components/theme/ThemeProvider';
import { resolveAccountLogoSrc, WEB_ASSETS } from '@tenfour/contracts';
import { useState, useRef, useEffect, useId, useCallback } from 'react';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { useModalA11y } from '@/hooks/useModalA11y';
import { ChromeIcon } from '../ChromeIcon';
import { BrandLockup } from '../BrandLockup';
import { PageHeaderIcon } from './PageHeaderIcon';
import {
  SETTINGS_GROUPS,
  SETTINGS_GROUP_LABELS,
} from '@tenfour/contracts';
import { PORTAL_NAV_GROUPS } from './portalNav';
import { isPortalNavActive } from './portalNav';
import { SettingsSearch } from './SettingsSearch';
import { useSettingsPages } from './useSettingsPages';
import styles from './Sidebar.module.css';

/** Shared chrome reliefs stay compact; destination marks use PageHeaderIcon. */
const CHROME_ICON_SIZE = 18;

// Primary destinations come from portalNav.ts so the public signup miniature
// carries the same order, groups, and PageHeaderIcon route keys as the live rail.

interface SidebarProps {
  isMobileOpen?: boolean;
  onHelpClick?: () => void;
  onClose?: () => void;
  /** Desktop collapse: the rail slides off-canvas and content reclaims its width. */
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

export function Sidebar({
  isMobileOpen = false,
  onClose,
  onHelpClick,
  collapsed = false,
  onToggleCollapse,
}: SidebarProps) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, accountId, signOut } = useAuth();
  const palette = useCommandPalette();
  // Which Settings sub-pages this user may see (ownerOnly → owner; adjustOnly →
  // the canAdjustSettings capability). The whole Settings group is hidden when
  // none are accessible — e.g. a Field Tech (#186).
  const visibleSettingsPages = useSettingsPages();
  // The search above the rail is handed the same gate result the rail uses, so
  // it cannot offer a setting the rail is hiding from this user (#186).
  const visibleSettingsPageIds = visibleSettingsPages.map((p) => p.id);
  const isAccountOwner = user?.accountRole !== 'MEMBER' && user?.role !== 'MEMBER';

  // The count of what is waiting on the Needs your attention item — the same
  // useNeedsYou feed Home's For you card and the mobile Attention tab read.
  const feed = useNeedsYou(accountId ?? '');
  const navGroupId = useId();

  // The org identity in the header row — business name + uploaded logo. Shared
  // React Query cache (key ['account', id]) with the settings pages, so an
  // edit there refreshes the rail without a reload.
  const { data: account } = useAccount(accountId ?? '');
  const businessName = account?.businessName?.trim() || 'Your business';
  // Uploaded logo wins; otherwise one of the outreach-signup workspace icons
  // (same clay set as the business-step preset picker), stable per account.
  const logoSrc = resolveAccountLogoSrc(
    account?.logoUrl,
    accountId ?? businessName,
  );

  // Red dot on the Money item (and the settings pane's Payments row) when the
  // account has a Stripe account but capabilities are disabled (charges or
  // payouts off) — Stripe marks accounts restricted when requirements go past
  // due, and without a cue accounts don't notice until a payment fails. Kept on
  // its own fetch because the Stripe capability fields aren't part of the typed
  // AccountProfile.
  const [paymentsAlert, setPaymentsAlert] = useState(false);

  useEffect(() => {
    if (!accountId) return;
    getAccountProfile(accountId).then((p: any) => {
      const hasStripeAccount = !!p.stripeAccountId;
      const capabilitiesDisabled = !p.stripeChargesEnabled || !p.stripePayoutsEnabled;
      setPaymentsAlert(hasStripeAccount && capabilitiesDisabled);
    }).catch(() => {});
  }, [accountId]);

  // Older addresses (/conversations, /customers/:id) light their destination.
  const isActive = (href: string) => isPortalNavActive(pathname, href);

  const isSettingsSubActive = (href: string) =>
    href === '/settings' ? pathname === '/settings' : pathname.startsWith(href);

  // The rail has two panes — regular nav and the settings list — swapped with a
  // horizontal slide. The mode is derived from the route so deep links land on
  // the right pane and browser back animates correctly.
  const settingsMode = pathname.startsWith('/settings');

  // Where "Back" returns to: the last non-settings page visited this session.
  const lastNonSettingsPath = useRef('/home');
  useEffect(() => {
    if (!pathname.startsWith('/settings')) lastNonSettingsPath.current = pathname;
  }, [pathname]);

  const openSettings = () => {
    if (!settingsMode && visibleSettingsPages.length > 0) {
      navigate('/settings/overview');
    }
  };
  const closeSettings = () => {
    if (settingsMode) navigate(lastNonSettingsPath.current);
  };

  // The org menu hanging off the org row's chevron — Add organization, Dark
  // mode, Account settings, Help & support, Sign out. Closes on an outside
  // mousedown and on Escape, with focus returned to the trigger.
  const [orgMenuOpen, setOrgMenuOpen] = useState(false);
  const orgSectionRef = useRef<HTMLDivElement>(null);
  const orgBtnRef = useRef<HTMLButtonElement>(null);
  const orgMenuOpenRef = useRef(orgMenuOpen);
  orgMenuOpenRef.current = orgMenuOpen;
  const { resolved, toggleDarkMode } = useTheme();
  const darkMode = resolved === 'dark';

  // Tier drives two things in the rail: whether "Add organization" pitches an
  // upgrade, and the plan line under the user's name at the bottom.
  const { data: tierSummary } = useTierSummary(accountId ?? '');
  const nextPaidTier = useNextPaidTier(accountId);
  const { openUpgradeModal } = useUpgradeModal();
  const isAssistant = tierSummary?.tier === 'GROWTH';
  // Plan names come from the runtime catalog and nowhere else. Until it
  // answers, the line stays blank — the same thing it does before the summary
  // loads — rather than naming a plan the catalog has not confirmed.
  const currentTierName = useTierLabel(accountId, tierSummary?.tier ?? 'GROWTH');
  const pendingTierName = useTierLabel(accountId, tierSummary?.pendingTier ?? 'GROWTH');
  const planLine =
    tierSummary && currentTierName
      ? tierSummary.pendingTier && pendingTierName
        ? `${currentTierName} → ${pendingTierName} next cycle`
        : currentTierName
      : '';

  useEffect(() => {
    if (!orgMenuOpen) return;
    function handlePointer(e: MouseEvent) {
      if (orgSectionRef.current && !orgSectionRef.current.contains(e.target as Node)) {
        setOrgMenuOpen(false);
      }
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOrgMenuOpen(false);
        orgBtnRef.current?.focus();
      }
    }
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [orgMenuOpen]);

  // The rail is off screen twice over: collapsed on desktop, and as the closed
  // drawer on a phone. Either way it goes inert, so Tab and screen readers
  // skip its controls instead of landing on buttons nobody can see. A shortcut
  // (/ or ⌘K) that opens the palette over the open drawer takes the drawer off
  // in the same commit (its trap lets go and it goes inert, so focus cannot
  // fall back into it): the palette's own trap owns focus and Tab. The drawer
  // then closes behind it (below), as the rail's Quick actions and Search
  // buttons already do.
  const isMobile = useIsMobile();
  const drawerOpen = isMobile && isMobileOpen && !palette.mode;
  const offscreen = isMobile ? !drawerOpen : collapsed;

  // The open phone drawer is modal (useModalA11y): focus moves in, Tab stays
  // inside, Escape closes, and focus returns to the menu button. Escape takes
  // the org menu down first when it is open. The layout hands us a fresh
  // onClose each render, so the hook gets a stable wrapper; otherwise every
  // shell re-render would bounce focus out of the drawer and back.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const dismissDrawer = useCallback(() => {
    if (orgMenuOpenRef.current) {
      setOrgMenuOpen(false);
      orgBtnRef.current?.focus();
      return;
    }
    onCloseRef.current?.();
  }, []);
  const sidebarRef = useModalA11y<HTMLElement>(drawerOpen, dismissDrawer, {
    returnFocus: () => document.querySelector<HTMLElement>('[data-drawer-trigger]'),
  });
  useEffect(() => {
    if (palette.mode && isMobileOpen) onCloseRef.current?.();
  }, [palette.mode, isMobileOpen]);

  // Desktop collapse moves focus with the control: collapsing hands it to the
  // header's Open sidebar button (the rail it sat in is now inert), expanding
  // hands it back to Collapse sidebar (the expand button shrinks to nothing).
  const collapseBtnRef = useRef<HTMLButtonElement>(null);
  const prevCollapsed = useRef(collapsed);
  useEffect(() => {
    if (prevCollapsed.current === collapsed) return;
    prevCollapsed.current = collapsed;
    if (isMobile) return;
    const active = document.activeElement;
    const lost = !active || active === document.body;
    if (collapsed) {
      if (lost || sidebarRef.current?.contains(active)) {
        document.querySelector<HTMLElement>('[data-sidebar-expand]')?.focus();
      }
    } else if (lost || active?.closest('[data-sidebar-expand]')) {
      collapseBtnRef.current?.focus();
    }
  }, [collapsed, isMobile, sidebarRef]);

  // The settings pane keeps its current page in view: on a short rail a deep
  // link (Ten Four billing) otherwise sits below the fold, selected but unseen.
  // It re-checks while the rail settles (the page list, setup checklist and
  // plan widget load after the route), and only scrolls when the row is out.
  const settingsNavRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const nav = settingsNavRef.current;
    if (!settingsMode || !nav) return;
    const reveal = () => {
      const current = nav.querySelector<HTMLElement>('[aria-current="page"]');
      if (!current) return;
      const navBox = nav.getBoundingClientRect();
      const box = current.getBoundingClientRect();
      if (box.top >= navBox.top && box.bottom <= navBox.bottom) return;
      nav.scrollTop += box.top - navBox.top - (navBox.height - box.height) / 2;
    };
    reveal();
    const observer = new ResizeObserver(reveal);
    observer.observe(nav);
    if (nav.firstElementChild) observer.observe(nav.firstElementChild);
    return () => observer.disconnect();
  }, [pathname, settingsMode]);

  // The palette owns every create action the retired Create launcher had, so
  // both rail buttons just open it (closing the mobile drawer behind them).
  const openPalette = (mode: 'actions' | 'search') => {
    onClose?.();
    palette.open(mode);
  };

  return (
    <>
      {/* Always mounted so the scrim fades both ways instead of popping. */}
      <div
        className={[styles.overlay, isMobileOpen ? styles.overlayOpen : ''].join(' ')}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        ref={sidebarRef}
        className={[
          styles.sidebar,
          isMobileOpen ? styles.open : '',
          collapsed ? styles.collapsed : '',
        ].join(' ')}
        {...(drawerOpen ? { role: 'dialog', 'aria-modal': true, 'aria-label': 'Menu' } : {})}
        {...(offscreen ? { inert: '' as const } : {})}
      >
        {/* Ten Four lockup ↔ Back (the lockup slides out left when the
            settings pane is open, and a back button slides in from the right to
            take its place). The collapse control is a sibling of both layers, so
            it stays put through that swap. */}
        <div className={styles.logoHeader}>
          <div
            className={[styles.headerLayer, settingsMode ? styles.headerLayerOut : ''].join(' ')}
          >
            <Link
              to="/home"
              className={styles.wordmark}
              aria-label="Ten Four home"
              tabIndex={settingsMode ? -1 : 0}
            >
              <BrandLockup label={null} />
            </Link>
          </div>
          <div
            className={[
              styles.headerLayer,
              styles.backLayer,
              settingsMode ? '' : styles.headerLayerWaiting,
            ].join(' ')}
          >
            <button
              type="button"
              className={styles.backBtn}
              onClick={closeSettings}
              tabIndex={settingsMode ? 0 : -1}
            >
              <ChromeIcon name="back" size={CHROME_ICON_SIZE} tone="muted" />
              Back
            </button>
          </div>
          <button
            type="button"
            className={styles.mobileClose}
            aria-label="Close menu"
            onClick={onClose}
          >
            <ChromeIcon name="close" size={20} />
          </button>
          {/* Desktop only — at ≤768px the rail is already an off-canvas drawer
              with its own menu and close controls. */}
          {onToggleCollapse && (
            <button
              ref={collapseBtnRef}
              type="button"
              className={[styles.collapseBtn, styles.tipped].join(' ')}
              aria-label="Collapse sidebar"
              onClick={onToggleCollapse}
            >
              <ChromeIcon name="sidebarClose" size={18} tone="muted" />
              <span className={[styles.tip, styles.tipBelow].join(' ')} role="tooltip">
                Collapse sidebar
              </span>
            </button>
          )}
        </div>

        {/* Org identity — the account's logo (or a default workspace icon) +
            business name, on the same white-pill-on-hairline surface as the
            Quick actions row below it so the two read as one block. Its chevron
            opens the account menu. */}
        <div
          className={[styles.orgRow, settingsMode ? styles.orgRowHidden : ''].join(' ')}
          ref={orgSectionRef}
        >
          <button
            ref={orgBtnRef}
            type="button"
            className={styles.orgBtn}
            onClick={() => setOrgMenuOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={orgMenuOpen}
            tabIndex={settingsMode ? -1 : 0}
          >
            <img src={logoSrc} alt="" className={styles.orgLogo} />
            <span className={styles.orgName}>{businessName}</span>
            <ChromeIcon
              name="chevronDown"
              size={14}
              className={[styles.orgChevron, orgMenuOpen ? styles.orgChevronOpen : ''].join(' ')}
            />
          </button>
          <div
            role="menu"
            aria-label="Account"
            aria-hidden={!orgMenuOpen}
            className={[styles.orgMenu, orgMenuOpen ? styles.orgMenuOpen : ''].join(' ')}
          >
            <div className={styles.orgMenuHeader}>{user?.email || 'Signed in'}</div>
            {/* Running more than one business is not built yet — there is no
                second account to switch to. On the free tier that's an upgrade
                pitch; on a paid tier it must NOT be, or someone pays for a
                second org and gets nothing. An unresolved tier reads as paid. */}
            {isAssistant ? (
              <button
                type="button"
                role="menuitem"
                className={styles.orgMenuItem}
                tabIndex={orgMenuOpen ? 0 : -1}
                onClick={() => {
                  setOrgMenuOpen(false);
                  onClose?.();
                  openUpgradeModal({ requiredTier: nextPaidTier, feature: 'Multiple businesses' });
                }}
              >
                <ChromeIcon name="add" size={15} />
                Add organization
              </button>
            ) : (
              <div
                role="menuitem"
                aria-disabled
                className={[styles.orgMenuItem, styles.orgMenuItemDisabled].join(' ')}
              >
                <ChromeIcon name="add" size={15} />
                Add organization
                <span className={styles.orgMenuHint}>Coming soon</span>
              </div>
            )}
            <button
              type="button"
              role="menuitemcheckbox"
              aria-checked={darkMode}
              className={styles.orgMenuItem}
              tabIndex={orgMenuOpen ? 0 : -1}
              onClick={toggleDarkMode}
            >
              <ChromeIcon name="theme" size={15} />
              Dark mode
              <span
                className={[styles.orgSwitch, darkMode ? styles.orgSwitchOn : ''].join(' ')}
                aria-hidden
              />
            </button>
            <Link
              to="/settings"
              role="menuitem"
              className={styles.orgMenuItem}
              tabIndex={orgMenuOpen ? 0 : -1}
              onClick={() => { setOrgMenuOpen(false); onClose?.(); }}
            >
              <ChromeIcon name="settings" size={15} />
              Account settings
            </Link>
            <button
              type="button"
              role="menuitem"
              className={styles.orgMenuItem}
              tabIndex={orgMenuOpen ? 0 : -1}
              onClick={() => { onHelpClick?.(); setOrgMenuOpen(false); onClose?.(); }}
            >
              <ChromeIcon name="help" size={15} />
              Help &amp; support
            </button>
            <button
              type="button"
              role="menuitem"
              className={styles.orgMenuItem}
              tabIndex={orgMenuOpen ? 0 : -1}
              onClick={() => { signOut(); setOrgMenuOpen(false); }}
            >
              <ChromeIcon name="signOut" size={15} />
              Sign out
            </button>
          </div>
        </div>

        {/* Quick actions + Search — the palette's two entry points, mirroring its
            ⌘K and / shortcuts. The palette absorbed the old Create launcher, so
            every "make a new thing" action now lives one keystroke away. Hidden
            while the settings pane is showing. */}
        <div className={[styles.actionRow, settingsMode ? styles.actionRowHidden : ''].join(' ')}>
          <button
            type="button"
            className={styles.actionPill}
            onClick={() => openPalette('actions')}
            tabIndex={settingsMode ? -1 : 0}
          >
            <ChromeIcon
              name="quickActions"
              size={14}
              className={styles.actionPillIcon}
              tone="muted"
            />
            <span className={styles.actionPillLabel}>Quick actions</span>
            <kbd className={styles.actionChip}>{QUICK_ACTIONS_SHORTCUT}</kbd>
          </button>
          <button
            type="button"
            className={[styles.actionSquare, styles.tipped].join(' ')}
            onClick={() => openPalette('search')}
            aria-label="Search records"
            tabIndex={settingsMode ? -1 : 0}
          >
            <ChromeIcon name="search" size={14} tone="muted" />
            <kbd className={styles.actionChip}>/</kbd>
            <span className={[styles.tip, styles.tipBelow].join(' ')} role="tooltip">
              Search records
            </span>
          </button>
        </div>

        <div className={styles.slideViewport}>
        <nav
          className={[styles.nav, styles.pane, settingsMode ? styles.paneOutLeft : styles.paneActive].join(' ')}
          aria-hidden={settingsMode}
          aria-label="Main"
        >
          {/* Four runs, each tinting its tiles one tone: the day (unlabeled),
              Customers, Your business, and Scheduling last. The current row
              gets the field-grey wash plus a short bar in its group's tone —
              the Needs your attention row-selection vocabulary. */}
          {PORTAL_NAV_GROUPS.map((group) => {
            const labelId = `${navGroupId}-${group.id}`;
            return (
              <div
                key={group.id}
                className={styles.navGroup}
                role={group.label ? 'group' : undefined}
                aria-labelledby={group.label ? labelId : undefined}
              >
                {group.label ? (
                  <div id={labelId} className={styles.navGroupLabel}>
                    {group.label}
                  </div>
                ) : null}
                <div className={styles.navList}>
                  {group.items.map((item) => {
                    const active = isActive(item.href);
                    return (
                      <Link
                        key={item.href}
                        to={item.href}
                        className={[styles.navLink, active ? styles.active : ''].join(' ')}
                        data-tone={group.tone}
                        aria-current={active ? 'page' : undefined}
                        onClick={onClose}
                      >
                        <PageHeaderIcon
                          pathname={item.href}
                          state={active ? 'active' : 'idle'}
                          tone={group.tone}
                          className={styles.navTile}
                        />
                        <span className={styles.navLabel}>{item.label}</span>
                        {item.href === '/queue' && feed.count > 0 ? (
                          <ClayCountBadge
                            count={feed.count}
                            label={`${feed.count} waiting`}
                            className={styles.navTrail}
                          />
                        ) : item.href === '/money' && paymentsAlert ? (
                          // Named, not just titled: on phones (no Money tab dot)
                          // this is the only signal off Home, so the link has to
                          // read "Money, Stripe needs attention".
                          <span
                            role="img"
                            aria-label="Stripe needs attention"
                            className={[styles.alertDot, styles.navTrail].join(' ')}
                            title="Stripe needs attention"
                          />
                        ) : null}
                      </Link>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </nav>

        {/* Settings pane — slides in from the right over the regular nav */}
        <nav
          ref={settingsNavRef}
          className={[styles.nav, styles.pane, settingsMode ? styles.paneActive : styles.paneOutRight].join(' ')}
          aria-hidden={!settingsMode}
          aria-label="Settings"
        >
          <div className={styles.section}>
            <div className={styles.sectionLabel}>Settings</div>
            <SettingsSearch
              accountId={accountId}
              visiblePages={visibleSettingsPageIds}
              isOwner={isAccountOwner}
              onNavigate={onClose}
              tabIndex={settingsMode ? 0 : -1}
            />
            <Link
              to="/settings/overview"
              className={[styles.navLink, pathname === '/settings/overview' ? styles.active : ''].join(' ')}
              aria-current={pathname === '/settings/overview' ? 'page' : undefined}
              onClick={onClose}
              tabIndex={settingsMode ? 0 : -1}
            >
              <ChromeIcon name="settings" tone="muted" size={18} />
              All settings
            </Link>
            {/* Grouped rather than one flat run of twelve: the rail reads as four
                decisions, ordered by how often a contractor touches them. Groups
                with nothing visible to this user collapse away entirely. */}
            {SETTINGS_GROUPS.map((group) => {
              const pages = visibleSettingsPages.filter((p) => p.group === group);
              if (!pages.length) return null;
              return (
                <div key={group} className={styles.settingsGroup}>
                  <div className={styles.settingsGroupLabel}>{SETTINGS_GROUP_LABELS[group]}</div>
                  <div className={styles.navList}>
                    {pages.map((item) => {
                      const active = isSettingsSubActive(item.href);
                      return (
                        <Link
                          key={item.href}
                          to={item.href}
                          className={[styles.navLink, active ? styles.active : ''].join(' ')}
                          aria-current={active ? 'page' : undefined}
                          onClick={onClose}
                          tabIndex={settingsMode ? 0 : -1}
                        >
                          <SettingsIcon page={item} />
                          {item.label}
                          {item.href === '/settings/payments' && paymentsAlert ? (
                            <span
                              role="img"
                              aria-label="Stripe needs attention"
                              className={styles.alertDot}
                              title="Stripe needs attention"
                            />
                          ) : undefined}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </nav>
        </div>

        {/* Setup checklist — the rail IS the setup surface now that
            /setup is gone, so it sits above the plan widget where a new owner
            meets it on every page. Removes itself once every item is done or
            skipped, which is why it is first in the bottom stack rather than
            reserving space forever. Its slot is the one part of that stack
            allowed to give up height, so on a short window the destinations
            keep their floor and the checklist scrolls instead. */}
        <div className={styles.setupSlot}>
          <SetupChecklistWidget enabled={Boolean(accountId)} onNavigate={onClose} />
        </div>

        {/* Plan + usage widget (subscription tiers) — between the nav scroll
            area and the Settings row. */}
        {accountId ? <TierWidget accountId={accountId} onNavigate={onClose} /> : null}

        {/* The signed-in person (not the business — that's the org row up top):
            avatar, name, plan. The gear swaps the rail to the settings pane, so
            the whole row is hidden when the user has no accessible sub-pages
            (#186; Security is ungated, so in practice everyone sees one). */}
        {visibleSettingsPages.length > 0 && (
        <div className={styles.userRow}>
          <img src={WEB_ASSETS.signup.avatars[0]} alt="" className={styles.userAvatar} />
          <div className={styles.userMeta}>
            <span className={styles.userName}>{user?.name || user?.email || 'Signed in'}</span>
            {planLine && <span className={styles.userPlan}>{planLine}</span>}
          </div>
          <button
            type="button"
            className={[styles.userSettingsBtn, styles.tipped, settingsMode ? styles.active : ''].join(' ')}
            onClick={settingsMode ? closeSettings : openSettings}
            aria-expanded={settingsMode}
            aria-label={settingsMode ? 'Close settings' : 'Open settings'}
          >
            <ChromeIcon name="settings" size={CHROME_ICON_SIZE} tone="muted" />
            <span className={[styles.tip, styles.tipAbove].join(' ')} role="tooltip">
              {settingsMode ? 'Close settings' : 'Open settings'}
            </span>
          </button>
        </div>
        )}
      </aside>
    </>
  );
}
